import { Injectable } from '@angular/core';
import {
  NavigationBehaviorOptions,
  NavigationExtras,
  Router,
  UrlCreationOptions,
} from '@angular/router';
import { isValidLocale } from './locale.constants';
import { LanguageNavigationService } from './language-navigation.service';

@Injectable({
  providedIn: 'root',
})
export class RouterLocalePatchService {
  private patched = false;

  private readonly rootSegments = new Set([
    'about-us',
    'auctions-calendar',
    'blogs',
    'blog-details',
    'buyer',
    'contact',
    'international-auctions',
    'join-as-professional',
    'maintenance',
    'privacy',
    'seller',
    'terms-and-conditions',
    'terms-of-service',
    'terms-of-use',
  ]);

  constructor(
    private router: Router,
    private languageNavigationService: LanguageNavigationService
  ) {}

  patchRouter(): void {
    if (this.patched) {
      return;
    }
    this.patched = true;

    this.patchCreateUrlTree();
    this.patchNavigate();
    this.patchNavigateByUrl();
  }

  private patchCreateUrlTree(): void {
    const originalCreateUrlTree = this.router.createUrlTree.bind(this.router);

    (this.router as Router).createUrlTree = (
      commands: readonly any[],
      navigationExtras?: UrlCreationOptions
    ) => {
      const localizedCommands = this.localizeCommands(commands, navigationExtras);
      return originalCreateUrlTree(localizedCommands, navigationExtras);
    };
  }

  private patchNavigate(): void {
    const originalNavigate = this.router.navigate.bind(this.router);

    (this.router as Router).navigate = (
      commands: readonly any[],
      extras?: NavigationExtras
    ) => {
      const localizedCommands = this.localizeCommands(commands, extras);
      return originalNavigate(localizedCommands, extras);
    };
  }

  private patchNavigateByUrl(): void {
    const originalNavigateByUrl = this.router.navigateByUrl.bind(this.router);

    (this.router as Router).navigateByUrl = (
      url: string | ReturnType<Router['parseUrl']>,
      extras?: NavigationBehaviorOptions
    ) => {
      if (typeof url === 'string') {
        return originalNavigateByUrl(this.localizeUrlString(url), extras);
      }

      return originalNavigateByUrl(url, extras);
    };
  }

  private localizeCommands(
    commands: readonly any[],
    navigationExtras?: UrlCreationOptions
  ): any[] {
    if (!Array.isArray(commands) || commands.length === 0) {
      return commands as any[];
    }

    const localized = [...commands];
    const first = localized[0];

    if (typeof first !== 'string') {
      return localized;
    }

    if (this.isRelativeCommand(first)) {
      return localized;
    }

    if (first === '/') {
      if (localized.length > 1 && typeof localized[1] === 'string') {
        const maybeLocale = localized[1].toLowerCase();
        if (!isValidLocale(maybeLocale)) {
          localized[0] = this.localizeUrlString('/');
        }
      } else {
        localized[0] = this.localizeUrlString('/');
      }
      return localized;
    }

    if (first.startsWith('/')) {
      localized[0] = this.localizeUrlString(first);
      return localized;
    }

    if (this.shouldTreatAsRootPath(first)) {
      localized[0] = this.localizeUrlString(`/${first}`);
    }

    return localized;
  }

  private localizeUrlString(url: string): string {
    if (!url) {
      return url;
    }

    if (/^(https?:)?\/\//i.test(url) || url.startsWith('mailto:')) {
      return url;
    }

    if (!url.startsWith('/')) {
      return url;
    }

    if (this.shouldSkipLocalization(url)) {
      return url;
    }

    return this.languageNavigationService.toLocalizedUrl(url);
  }

  private shouldTreatAsRootPath(value: string): boolean {
    if (!value) {
      return false;
    }

    if (this.isRelativeCommand(value)) {
      return false;
    }

    if (/^(https?:)?\/\//i.test(value) || value.startsWith('mailto:')) {
      return false;
    }

    if (value.includes('/')) {
      return true;
    }

    return this.rootSegments.has(value);
  }

  private isRelativeCommand(value: string): boolean {
    return (
      value === '.' ||
      value === '..' ||
      value.startsWith('./') ||
      value.startsWith('../')
    );
  }

  private shouldSkipLocalization(url: string): boolean {
    const pathOnly = url.split(/[?#]/)[0] || '/';

    if (
      pathOnly === '/super-admin' ||
      pathOnly.startsWith('/super-admin/')
    ) {
      return true;
    }

    if (
      pathOnly === '/platform/redirect' ||
      pathOnly.startsWith('/platform/redirect/') ||
      pathOnly === '/redirect' ||
      pathOnly.startsWith('/redirect/')
    ) {
      return true;
    }

    if (
      pathOnly.startsWith('/assets/') ||
      pathOnly.startsWith('/media/') ||
      pathOnly.startsWith('/icons2/') ||
      pathOnly === '/manifest.webmanifest' ||
      pathOnly.startsWith('/.well-known/')
    ) {
      return true;
    }

    return false;
  }
}
